<template>
  <div class="mini-kasko-order__group mini-kasko-order__group--wide">
    <h2 class="mini-kasko-order__group-title">Автомобіль</h2>
    <div class="mini-kasko-order__group-grid">
      <div class="mini-kasko-order__field">
        <span>Держномер</span>
        <div class="mini-kasko-order__field-with-action">
          <input
            ref="reg_num"
            :value="value.reg_num"
            :class="{ 'mini-kasko-order__input--invalid': Boolean(errors.reg_num || regNumError) }"
            type="text"
            :readonly="isDeclared"
            @input="handleRegNumInput"
            @blur="handleRegNumBlur"
          >
          <button
            type="button"
            class="mini-kasko-order__lookup-button"
            :disabled="isDeclared || lookupLoading || !value.reg_num"
            @click="handleLookupClick"
          >
            {{ lookupLoading ? 'Пошук...' : 'Знайти' }}
          </button>
        </div>
        <small v-if="errors.reg_num || regNumError" class="mini-kasko-order__field-error">
          {{ errors.reg_num || regNumError }}
        </small>
      </div>
      <label class="mini-kasko-order__field">
        <span>VIN</span>
        <input
          ref="vin"
          :value="value.vin"
          :class="{ 'mini-kasko-order__input--invalid': Boolean(errors.vin) }"
          type="text"
          maxlength="17"
          :readonly="isDeclared"
          @input="emitFieldInput('vin', $event)"
        >
        <small v-if="errors.vin" class="mini-kasko-order__field-error">{{ errors.vin }}</small>
      </label>
      <label class="mini-kasko-order__field">
        <span>Рік випуску</span>
        <input
          ref="year"
          :value="value.year"
          :class="{ 'mini-kasko-order__input--invalid': Boolean(errors.year) }"
          type="number"
          min="1900"
          max="2100"
          :readonly="isDeclared"
          @input="emitFieldInput('year', $event)"
        >
        <small v-if="errors.year" class="mini-kasko-order__field-error">{{ errors.year }}</small>
      </label>
      <label class="mini-kasko-order__field">
        <span>Марка</span>
        <input
          ref="mark_name"
          :value="value.mark_name"
          :class="{ 'mini-kasko-order__input--invalid': Boolean(errors.mark_name) }"
          type="text"
          :readonly="isDeclared"
          @input="emitFieldInput('mark_name', $event)"
        >
        <small v-if="errors.mark_name" class="mini-kasko-order__field-error">{{ errors.mark_name }}</small>
      </label>
      <label class="mini-kasko-order__field">
        <span>Модель</span>
        <input
          ref="model_name"
          :value="value.model_name"
          :class="{ 'mini-kasko-order__input--invalid': Boolean(errors.model_name) }"
          type="text"
          :readonly="isDeclared"
          @input="emitFieldInput('model_name', $event)"
        >
        <small v-if="errors.model_name" class="mini-kasko-order__field-error">{{ errors.model_name }}</small>
      </label>
      <div class="mini-kasko-order__field">
        <span>Тип авто</span>
        <MiniKaskoCarTypeField
          ref="category"
          :value="value.category"
          :disabled="isDeclared"
          :error="errors.category"
          @input="handleCategoryInput"
        />
      </div>
      <div class="mini-kasko-order__field">
        <span>Місце реєстрації авто</span>
        <MiniKaskoCityField
          ref="registration_city_id"
          :value="value.registration_city_id"
          :selected-label="value.registration_city_name"
          :disabled="isDeclared"
          :error="errors.registration_city_id"
          @input="handleRegistrationCityInput"
          @change="handleRegistrationCityChange"
        />
        <button
          v-if="showRecalculate && !isDeclared"
          type="button"
          class="mini-kasko-order__recalculate-btn"
          @click="$emit('recalculate')"
        >
          Перерахувати з новим містом
        </button>
      </div>
      <div class="mini-kasko-order__switch-field">
        <span class="mini-kasko-order__switch-label">Таксі</span>
        <label
          ref="is_taxi"
          class="mini-kasko-order__switch"
          :class="{ 'mini-kasko-order__switch--invalid': Boolean(errors.is_taxi) }"
        >
          <input
            :checked="Boolean(Number(value.is_taxi))"
            type="checkbox"
            :disabled="isDeclared"
            @change="handleTaxiChange"
          >
          <span class="mini-kasko-order__switch-box">
            <span class="mini-kasko-order__switch-control"/>
            <span class="mini-kasko-order__switch-text">
              {{ Number(value.is_taxi) ? 'Авто використовується як таксі' : 'Авто не використовується як таксі' }}
            </span>
          </span>
        </label>
        <small v-if="errors.is_taxi" class="mini-kasko-order__field-error">{{ errors.is_taxi }}</small>
      </div>
    </div>
  </div>
</template>

<script>
import {autoNumberValidate} from '@/mixin';
import {getCarInfoByNumber} from '@/api/car';
import MiniKaskoCarTypeField from "@/views/miniKasko/components/MiniKaskoCarTypeField.vue";
import MiniKaskoCityField from "@/views/miniKasko/components/MiniKaskoCityField.vue";

export default {
  name: 'MiniKaskoCarForm',
  mixins: [autoNumberValidate],
  components: {MiniKaskoCarTypeField, MiniKaskoCityField},
  props: {
    value: {
      type: Object,
      required: true,
    },
    isDeclared: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    showRecalculate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      autoNumberValidatePatternGroup: ['vehicle', 'trailer', 'vr', 'mvs', 'diplomatic', 'military'],
      regNumError: '',
      regNumValidationResult: null,
      autoNumberPatternsPromise: null,
      lookupLoading: false,
    };
  },
  created() {
    this.ensureAutoNumberPatternsLoaded();
  },
  watch: {
    value: {
      deep: true,
      handler() {
        this.resetRegNumValidationState();
      },
    },
  },
  methods: {
    buildNextValue(field, value) {
      return {
        ...this.value,
        [field]: value,
      };
    },
    async ensureAutoNumberPatternsLoaded() {
      if (Object.keys(this.patterns || {}).length) {
        return;
      }

      if (!this.autoNumberPatternsPromise) {
        this.autoNumberPatternsPromise = this.$store.dispatch('loadPatterns')
          .finally(() => {
            this.autoNumberPatternsPromise = null;
          });
      }

      await this.autoNumberPatternsPromise;
    },
    hasMixedLayouts(value) {
      const latinPattern = /[a-zA-Z]/;
      const cyrillicPattern = /[а-яА-ЯІіЇїЄє]/;
      const hasLatin = latinPattern.test(value);
      const hasCyrillic = cyrillicPattern.test(value);

      return hasLatin && hasCyrillic;
    },
    emitFieldInput(field, event) {
      const rawValue = event?.target?.value || '';
      const value = field === 'year' ? String(rawValue) : String(rawValue).trim();

      this.$emit('input', this.buildNextValue(field, value));
      this.$emit('field-change', field);
    },
    handleRegNumInput(event) {
      const value = String(event?.target?.value || '').toUpperCase().replace(/\s+/g, '');

      this.$emit('input', this.buildNextValue('reg_num', value));
      this.$emit('field-change', 'reg_num');
      this.resetRegNumValidationState();
    },
    handleCategoryInput(value) {
      this.$emit('input', this.buildNextValue('category', value));
      this.$emit('field-change', 'category');
    },
    handleTaxiChange(event) {
      this.$emit('input', this.buildNextValue('is_taxi', event?.target?.checked ? 1 : 0));
      this.$emit('field-change', 'is_taxi');
    },
    handleRegistrationCityInput(value) {
      this.$emit('input', this.buildNextValue('registration_city_id', value));
      this.$emit('field-change', 'registration_city_id');
    },
    handleRegistrationCityChange(city) {
      this.$emit('input', {
        ...this.value,
        registration_city_id: city?.id || this.value.registration_city_id,
        registration_city_name: city?.name_full_name_ua || '',
      });
      this.$emit('field-change', 'registration_city_id');
    },
    resetRegNumValidationState() {
      this.regNumError = '';
      this.regNumValidationResult = null;
    },
    resolveLookupCity(city) {
      if (!city || typeof city !== 'object') {
        return {};
      }

      return {
        id: city.id || city.city_id || null,
        name: city.name_full_name_ua || city.name_ua || city.name || '',
      };
    },
    applyLookupResult(result) {
      const city = this.resolveLookupCity(result.city);
      const nextValue = {
        ...this.value,
        mark_name: result.mark || this.value.mark_name,
        model_name: result.model || this.value.model_name,
        year: result.year || this.value.year,
        vin: result.vin || this.value.vin,
        category: result.type || this.value.category,
        registration_city_id: city.id || this.value.registration_city_id,
        registration_city_name: city.name || this.value.registration_city_name,
      };

      this.$emit('input', nextValue);
      ['mark_name', 'model_name', 'year', 'vin', 'category', 'registration_city_id'].forEach((field) => {
        this.$emit('field-change', field);
      });
    },
    async validateRegNum() {
      const regNum = String(this.value.reg_num || '').toUpperCase().replace(/\s+/g, '');

      if (regNum !== this.value.reg_num) {
        this.$emit('input', this.buildNextValue('reg_num', regNum));
      }

      if (!regNum || this.isDeclared) {
        this.resetRegNumValidationState();
        return true;
      }

      if (this.hasMixedLayouts(regNum)) {
        this.regNumError = 'Введіть держномер тільки латиницею або тільки кирилицею.';
        this.regNumValidationResult = {
          value: regNum,
          isValid: false,
          message: this.regNumError,
        };

        return false;
      }

      await this.ensureAutoNumberPatternsLoaded();

      const isValid = await this.validateCarNumber(regNum, this.autoNumberValidatePatternGroup);

      this.regNumError = isValid ? '' : 'Некоректний формат держномера.';
      this.regNumValidationResult = {
        value: regNum,
        isValid,
        message: this.regNumError,
      };

      return isValid;
    },
    async handleRegNumBlur() {
      await this.validateRegNum();
    },
    async handleLookupClick() {
      if (this.isDeclared || !this.value.reg_num || this.lookupLoading) {
        return;
      }

      const isValid = await this.validateRegNum();
      if (!isValid) {
        return;
      }

      this.lookupLoading = true;

      try {
        const response = await getCarInfoByNumber(this.value.reg_num);
        const result = response?.data?.data;

        if (!result) {
          this.regNumError = 'За вказаним держномером дані авто не знайдено.';
          return;
        }

        this.regNumError = '';
        this.applyLookupResult(result);
      } catch (error) {
        this.regNumError = error?.response?.data?.message || 'Не вдалося отримати дані авто за держномером.';
      } finally {
        this.lookupLoading = false;
      }
    },
    focus(field) {
      const ref = this.$refs[field];
      const input = ref && typeof ref.querySelector === 'function'
        ? ref.querySelector('input, button, select, textarea')
        : ref;
      const scrollTarget = ref && ref.$el ? ref.$el : ref;

      if (!input && !scrollTarget) {
        return false;
      }

      if (scrollTarget && typeof scrollTarget.scrollIntoView === 'function') {
        scrollTarget.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      if (input && typeof input.focus === 'function') {
        input.focus();
      }

      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
.mini-kasko-order {
  &__group {
    display: flex;
    flex-direction: column;
    gap: 12px;

    &--wide {
      grid-column: span 2;
    }
  }

  &__group-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 16px;
  }

  &__group-title {
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    span {
      font-size: 13px;
      line-height: 18px;
      color: #52525b;
    }

    input,
    select {
      width: 100%;
      min-height: 44px;
      padding: 10px 12px;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      background: #fff;
      outline: none;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;

      &:focus {
        border-color: #fb923c;
        box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.15);
      }

      &[readonly],
      &:disabled {
        background: #f8fafc;
        color: #52525b;
        cursor: default;
      }
    }

    .mini-kasko-order__input--invalid {
      border-color: #ef4444;

      &:focus {
        border-color: #ef4444;
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
      }
    }
  }

  &__switch-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    grid-column: span 2;
  }

  &__switch-label {
    font-size: 13px;
    line-height: 18px;
    color: #52525b;
  }

  &__switch {
    display: block;
    width: 100%;

    input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }
  }

  &__switch-box {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 44px;
    padding: 10px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  }

  &__switch-control {
    position: relative;
    flex: 0 0 42px;
    width: 42px;
    height: 24px;
    border-radius: 999px;
    background: #cbd5e1;
    transition: background-color 0.2s ease;

    &::after {
      content: '';
      position: absolute;
      top: 3px;
      left: 3px;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #fff;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.2);
      transition: transform 0.2s ease;
    }
  }

  &__switch-text {
    font-size: 14px;
    line-height: 20px;
    color: #0f172a;
  }

  &__switch input:checked + &__switch-box {
    background: #fff7ed;
    border-color: #fdba74;
  }

  &__switch input:checked + &__switch-box &__switch-control {
    background: #f97316;

    &::after {
      transform: translateX(18px);
    }
  }

  &__switch input:focus + &__switch-box,
  &__switch input:focus-visible + &__switch-box {
    border-color: #fb923c;
    box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.15);
  }

  &__switch input:disabled + &__switch-box {
    background: #f8fafc;
    cursor: default;
  }

  &__switch--invalid &__switch-box {
    border-color: #ef4444;
  }

  &__switch--invalid input:focus + &__switch-box,
  &__switch--invalid input:focus-visible + &__switch-box {
    border-color: #ef4444;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
  }

  &__field-with-action {
    display: flex;
    gap: 8px;

    input {
      flex: 1 1 auto;
    }
  }

  &__lookup-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    min-width: 92px;
    min-height: 44px;
    padding: 0 14px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #f8fafc;
    color: #1e293b;
    font-size: 14px;
    font-weight: 600;
    transition: background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;

    &:hover:not(:disabled) {
      background: #eef2ff;
      border-color: #94a3b8;
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  &__field-error {
    color: #dc2626;
    font-size: 12px;
    line-height: 16px;
  }

  &__recalculate-btn {
    align-self: flex-start;
    padding: 6px 14px;
    border-radius: 6px;
    border: 1px solid #f59e0b;
    background: #fffbeb;
    color: #92400e;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.2s ease, border-color 0.2s ease;

    &:hover {
      background: #fef3c7;
      border-color: #d97706;
    }
  }

  @media (max-width: 960px) {
    &__group-grid {
      grid-template-columns: 1fr;
    }

    &__group--wide {
      grid-column: span 1;
    }

    &__switch-field {
      grid-column: span 1;
    }
  }

  @media (max-width: 640px) {
    &__field-with-action {
      flex-direction: column;
    }
  }
}
</style>
